export interface ServiceCategoryOption {
  id: string;
  label: string;
}

/**
 * Which read a service row came from. The two reads that feed the appointment
 * journey do not carry the same fields, so the published-only rule has to know.
 *
 *   studio  the admin Services read. `status` is a REQUIRED contract field
 *           (`draft | published`), so a row without it is not known to be
 *           published and is not offered.
 *   public  the public /catalog read. It publishes no `status` at all -- the
 *           server decides what a visitor may book -- so a row is dropped here
 *           only when it explicitly names another status, never for omitting one.
 */
export type ServiceStatusSource = "studio" | "public";

/**
 * Whether a service may be offered on the customer-facing appointment journey.
 *
 * Services admin shows every status; the journey -- the designer preview and the
 * public booking page alike -- offers only published services. Applied at the
 * catalogue boundary, before category options, counts, durations or selection
 * are derived, so a draft row cannot reach any of them.
 */
export function isPublishedService(row: { status?: unknown } | null | undefined, source: ServiceStatusSource): boolean {
  if (!row || typeof row !== "object") return false;
  const status = String(row.status ?? "").trim().toLowerCase();
  if (!status) return source === "public";
  return status === "published";
}

/** The rows the appointment journey may offer, in their original order. */
export function publishedServicesOnly<T extends { status?: unknown }>(rows: readonly T[] | null | undefined, source: ServiceStatusSource): T[] {
  return (rows || []).filter((row) => isPublishedService(row, source));
}

/** Stable comparison key for a tenant-owned service category. */
export function serviceCategoryId(value: unknown): string {
  const label = String(value ?? "").trim().toLocaleLowerCase().replace(/\s+/g, " ");
  return label ? "category:" + label : "";
}

/** Filter key, retaining the reserved unfiltered `all` value. */
export function serviceFilterId(value: unknown): string {
  const text = String(value ?? "").trim();
  return text === "all" ? "all" : text.indexOf("category:") === 0 ? text : serviceCategoryId(text);
}

/** Build the picker from the categories the tenant actually returned. */
export function serviceCategoryOptions(services: Array<{ cat?: unknown }> | null | undefined): ServiceCategoryOption[] {
  const options: ServiceCategoryOption[] = [{ id: "all", label: "All" }];
  const seen = new Set<string>();
  for (const service of services || []) {
    const label = String(service?.cat ?? "").trim();
    const id = serviceCategoryId(label);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    options.push({ id, label });
  }
  return options;
}

/** Missing categories are intentionally visible only under the All filter. */
export function serviceMatchesCategory(service: { cat?: unknown }, selected: unknown): boolean {
  const id = serviceFilterId(selected);
  return id === "all" || (!!serviceCategoryId(service?.cat) && serviceCategoryId(service?.cat) === id);
}
